<template>
  <div class="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-4 dark:border-indigo-800 dark:from-indigo-900/30 dark:to-purple-900/30">
    <!-- Icon and Title -->
    <div class="mb-3 flex items-start gap-3">
      <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-800">
        <DocumentIcon class="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
      </div>
      <div class="flex-1 min-w-0">
        <h4 class="font-semibold text-slate-800 dark:text-slate-100">
          {{ actionTitle }}
        </h4>
        <p class="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
          {{ actionDescription }}
        </p>
      </div>
    </div>

    <!-- Summary of what will be created -->
    <div v-if="summaryItems.length > 0" class="mb-4 space-y-1.5">
      <div
        v-for="(item, index) in summaryItems"
        :key="index"
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
      >
        <CheckIcon class="h-4 w-4 text-indigo-500" />
        <span>{{ item }}</span>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex items-center justify-end gap-2">
      <Button
        label="Dismiss"
        severity="secondary"
        size="small"
        text
        @click="emit('dismiss')"
      />
      <Button
        label="Build"
        icon="pi pi-bolt"
        severity="primary"
        size="small"
        @click="emit('build')"
        :pt="{
          root: { class: 'bg-indigo-600 hover:bg-indigo-700' }
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { DocumentIcon, CheckIcon } from '@heroicons/vue/24/outline'
import type { RightyAction } from '../types'
import { summarizeAction } from '../services/rightyActionHandler'

const props = defineProps<{
  action: RightyAction
}>()

const emit = defineEmits<{
  build: []
  dismiss: []
}>()

const actionTitle = computed(() => {
  const type = props.action.type
  switch (type) {
    case 'create_dimension':
    case 'add_dimension':
      return 'Create Segment Group'
    case 'create_question':
    case 'add_question':
      return 'Create Question'
    case 'add_node_to_flow':
      return 'Add to Flow'
    case 'batch_operations':
      return 'Apply Changes'
    default:
      return 'Build Configuration'
  }
})

const actionDescription = computed(() => {
  const type = props.action.type
  switch (type) {
    case 'create_dimension':
    case 'add_dimension':
      return `Create a new segment group called "${props.action.data.name || 'New Group'}"`
    case 'create_question':
    case 'add_question':
      return `Add a question to gather "${props.action.data.dimensionName || 'information'}"`
    case 'add_node_to_flow':
      return 'Insert a new node into your flow'
    case 'batch_operations':
      const count = props.action.data.operations?.length || 0
      return `Apply ${count} ${count === 1 ? 'change' : 'changes'} to your configuration`
    default:
      return 'Ready to apply the generated configuration'
  }
})

const summaryItems = computed(() => {
  const items: string[] = []
  const summaries = summarizeAction(props.action)

  // Extract key points from summaries
  for (const summary of summaries.slice(0, 4)) {
    items.push(summary.title)
  }

  return items
})
</script>
